import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Modal } from '../../components/primitives';
import { ListPage } from '../../components/list';
import { StavbaGate } from '../../components/stavba';
import { useActiveStavba } from '../stavba-context/StavbaContext';
import {
  usePhotos,
  useUploadPhoto,
  useUpdatePhoto,
  useDeletePhoto,
  useDeletePhotosBulk,
} from '../../api/hooks';
import { api, getAuthToken } from '../../lib/api';
import type { StavbaPhoto } from '../../api/schemas';
import styles from './FotodokumentacePage.module.css';
import { cx } from '../../lib/cx';

const KAT_LABEL: Record<string, string> = {
  prubeh: 'Průběh',
  pred_po: 'Před/Po',
  vada: 'Vada',
  ostatni: 'Ostatní',
};

const VALID_LB_KAT = new Set(['prubeh', 'pred_po', 'vada', 'ostatni']);

const FILTERS: { id: string; label: string }[] = [
  { id: '', label: 'Vše' },
  { id: 'prubeh', label: 'Průběh' },
  { id: 'pred_po', label: 'Před/Po' },
  { id: 'vada', label: 'Vady' },
  { id: 'ostatni', label: 'Ostatní' },
  { id: 'denik', label: 'Deník' },
  { id: 'kd', label: 'Kontrolní den' },
];

function dayKey(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('cs-CZ', { day: '2-digit', month: '2-digit', year: 'numeric', weekday: 'long' });
}

function groupByDay(photos: StavbaPhoto[]): { key: string; items: StavbaPhoto[] }[] {
  const map = new Map<string, StavbaPhoto[]>();
  for (const p of photos) {
    const k = dayKey(p.datum ?? p.created_at);
    const arr = map.get(k) ?? [];
    arr.push(p);
    map.set(k, arr);
  }
  return [...map.entries()].map(([key, items]) => ({ key, items }));
}

function filterPhotos(photos: StavbaPhoto[], filterId: string): StavbaPhoto[] {
  if (!filterId) return photos;
  if (filterId === 'denik') return photos.filter((p) => p.denik_id != null);
  if (filterId === 'kd') return photos.filter((p) => p.kd_id != null);
  return photos.filter((p) => p.kategorie === filterId);
}

/** Auth-aware obrázek — stahuje binary přes fetch s JWT tokenem a ukáže blob URL. */
function PhotoThumb({ url, alt }: { url: string; alt: string }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    const token = getAuthToken();
    let blobUrl: string | null = null;
    let cancelled = false;
    fetch(url, token ? { headers: { Authorization: `Bearer ${token}` } } : undefined)
      .then((r) => (r.ok ? r.blob() : Promise.reject(r.status)))
      .then((b) => {
        if (cancelled) return;
        blobUrl = URL.createObjectURL(b);
        setSrc(blobUrl);
      })
      .catch(() => {
        /* ignore */
      });
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [url]);
  if (!src) return null;
  return <img src={src} alt={alt} className={styles.thumbImg} />;
}

function PhotoFull({ url, alt }: { url: string; alt: string }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    const token = getAuthToken();
    let blobUrl: string | null = null;
    let cancelled = false;
    fetch(url, token ? { headers: { Authorization: `Bearer ${token}` } } : undefined)
      .then((r) => (r.ok ? r.blob() : Promise.reject(r.status)))
      .then((b) => {
        if (cancelled) return;
        blobUrl = URL.createObjectURL(b);
        setSrc(blobUrl);
      })
      .catch(() => {
        /* ignore */
      });
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [url]);
  if (!src) return <div className={styles.lightboxLoading}>Načítám…</div>;
  return <img src={src} alt={alt} className={styles.lightboxImg} />;
}

export function FotodokumentacePage() {
  const { activeStavbaId, activeStavba } = useActiveStavba();
  const { data: photos = [], isLoading, error } = usePhotos(activeStavbaId);
  const upload = useUploadPhoto(activeStavbaId);
  const updatePhoto = useUpdatePhoto(activeStavbaId);
  const deletePhoto = useDeletePhoto(activeStavbaId);
  const deleteBulk = useDeletePhotosBulk(activeStavbaId);

  const fileInput = useRef<HTMLInputElement | null>(null);
  /** Zda další výběr souborů použije metadata z dialogu „Nahrát…“. */
  const uploadWithMetaRef = useRef(false);
  const [filterId, setFilterId] = useState('');
  const [selected, setSelected] = useState<Set<number>>(() => new Set());
  const [lightbox, setLightbox] = useState<StavbaPhoto | null>(null);
  const [lbPopis, setLbPopis] = useState('');
  const [lbKat, setLbKat] = useState('ostatni');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [upKat, setUpKat] = useState('ostatni');
  const [upPopis, setUpPopis] = useState('');
  const [upDatum, setUpDatum] = useState(() => new Date().toISOString().slice(0, 10));
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailMsg, setEmailMsg] = useState<string | null>(null);

  const filtered = useMemo(() => filterPhotos(photos, filterId), [photos, filterId]);
  const groups = useMemo(() => groupByDay(filtered), [filtered]);
  const total = filtered.length;

  const toggleSelect = useCallback((id: number, ev?: React.MouseEvent) => {
    ev?.stopPropagation();
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const deselectAll = useCallback(() => setSelected(new Set()), []);

  const openLightbox = useCallback((p: StavbaPhoto) => {
    setLightbox(p);
  }, []);

  useEffect(() => {
    if (!lightbox) return;
    setLbPopis(lightbox.popis ?? '');
    setLbKat(VALID_LB_KAT.has(lightbox.kategorie) ? lightbox.kategorie : 'ostatni');
  }, [lightbox]);

  async function handleLbSave() {
    if (!activeStavbaId || !lightbox) return;
    await updatePhoto.mutateAsync({
      photoId: lightbox.id,
      payload: { popis: lbPopis, kategorie: lbKat },
    });
    setLightbox((cur) =>
      cur && cur.id === lightbox.id ? { ...cur, popis: lbPopis, kategorie: lbKat } : cur,
    );
  }

  async function handleLbDelete() {
    if (!lightbox || !confirm('Opravdu smazat tuto fotku?')) return;
    const id = lightbox.id;
    await deletePhoto.mutateAsync(id);
    setSelected((s) => {
      const n = new Set(s);
      n.delete(id);
      return n;
    });
    setLightbox(null);
  }

  async function handleLbDownload() {
    if (!activeStavbaId || !lightbox) return;
    const token = getAuthToken();
    const url = `/api/stavby/${activeStavbaId}/photos/${lightbox.id}/full`;
    const r = await fetch(url, token ? { headers: { Authorization: `Bearer ${token}` } } : undefined);
    if (!r.ok) return;
    const blob = await r.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = lightbox.filename;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  async function downloadZip(onlySelected: boolean) {
    if (!activeStavbaId) return;
    if (onlySelected) {
      const ids = Array.from(selected);
      if (ids.length === 0) {
        alert('Vyberte alespoň jednu fotku.');
        return;
      }
      try {
        const blob = await api.post<Blob>(`/stavby/${activeStavbaId}/photos/download-zip`, ids);
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = 'fotodokumentace.zip';
        a.click();
        URL.revokeObjectURL(a.href);
      } catch {
        alert('Chyba při stahování ZIP.');
      }
      return;
    }
    try {
      const blob = await api.post<Blob>(`/stavby/${activeStavbaId}/photos/download-zip`, null);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = 'fotodokumentace.zip';
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      alert('Chyba při stahování ZIP.');
    }
  }

  async function deleteSelected() {
    if (!activeStavbaId) return;
    const ids = Array.from(selected);
    if (!ids.length) return;
    if (!confirm(`Opravdu smazat ${ids.length} fotek? Tuto akci nelze vrátit.`)) return;
    await deleteBulk.mutateAsync(ids);
    deselectAll();
  }

  async function sendEmail() {
    if (!activeStavbaId || !emailTo.trim()) return;
    const photoIds =
      selected.size > 0 ? Array.from(selected) : filtered.map((p) => p.id);
    if (!photoIds.length) {
      setEmailMsg('Žádné fotky k odeslání.');
      return;
    }
    setEmailBusy(true);
    setEmailMsg(null);
    try {
      await api.post(`/stavby/${activeStavbaId}/photos/email`, {
        to: emailTo.trim(),
        subject: emailSubject.trim(),
        body: emailBody.trim(),
        photo_ids: photoIds,
      });
      setEmailOpen(false);
      setEmailTo('');
      setEmailSubject('');
      setEmailBody('');
      alert('Odeslání bylo zařazeno do fronty. E-mail dorazí, pokud je na serveru nakonfigurováno SMTP.');
    } catch (e) {
      setEmailMsg(e instanceof Error ? e.message : 'Chyba při odesílání.');
    } finally {
      setEmailBusy(false);
    }
  }

  async function handleFileInputChange(files: FileList | null) {
    if (!files?.length || !activeStavbaId) return;
    const withMeta = uploadWithMetaRef.current;
    const datumIso = withMeta && upDatum ? `${upDatum}T12:00:00` : undefined;
    for (const file of Array.from(files)) {
      if (withMeta) {
        await upload.mutateAsync({
          file,
          kategorie: upKat,
          popis: upPopis,
          datum: datumIso,
        });
      } else {
        await upload.mutateAsync({ file });
      }
    }
    if (fileInput.current) fileInput.current.value = '';
    uploadWithMetaRef.current = false;
    if (withMeta) setUploadOpen(false);
  }

  const filterToolbar = (
    <div className={styles.filters}>
      {FILTERS.map((f) => (
        <button
          key={f.id || 'all'}
          type="button"
          className={cx(styles.filterBtn, filterId === f.id && styles.filterBtnActive)}
          onClick={() => setFilterId(f.id)}
        >
          {f.label}
        </button>
      ))}
      <span className={styles.filterCount}>{total} fotek</span>
    </div>
  );

  const selectionBar =
    selected.size > 0 ? (
      <div className={styles.selectionBar}>
        <strong>{selected.size}</strong> vybráno
        <Button variant="secondary" onClick={() => downloadZip(true)}>
          Stáhnout vybrané (ZIP)
        </Button>
        <Button variant="secondary" onClick={() => setEmailOpen(true)}>
          E-mail vybraných
        </Button>
        <Button variant="secondary" onClick={deleteSelected} disabled={deleteBulk.isPending}>
          Smazat vybrané
        </Button>
        <Button variant="ghost" onClick={deselectAll}>
          Zrušit výběr
        </Button>
      </div>
    ) : null;

  if (!activeStavbaId) {
    return (
      <ListPage breadcrumb={['Realizace', 'Fotodokumentace']} title="Fotodokumentace" subtitle="Vyberte nebo založte stavbu">
        <StavbaGate message="Fotodokumentace se eviduje k jedné stavbě." />
      </ListPage>
    );
  }

  return (
    <>
      <ListPage
        breadcrumb={['Realizace', activeStavba?.nazev ?? '', 'Fotodokumentace'].filter(Boolean)}
        title="Fotodokumentace"
        subtitle={
          isLoading
            ? 'Načítám…'
            : error
              ? 'Chyba při načítání'
              : `${activeStavba?.nazev ?? 'Stavba'} · ${photos.length} fotografií celkem`
        }
        status={isLoading ? 'neutral' : error ? 'draft' : 'done'}
        statusText={isLoading ? 'Načítám…' : error ? 'Chyba' : `${total} v aktuálním filtru`}
        actions={
          <>
            <input
              ref={fileInput}
              type="file"
              accept="image/*"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => handleFileInputChange(e.target.files)}
            />
            <Button variant="secondary" onClick={() => setUploadOpen(true)} disabled={upload.isPending}>
              Nahrát…
            </Button>
            <Button variant="secondary" onClick={() => downloadZip(false)}>
              Stáhnout ZIP (vše)
            </Button>
            <Button variant="secondary" onClick={() => setEmailOpen(true)}>
              E-mail
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                uploadWithMetaRef.current = false;
                fileInput.current?.click();
              }}
              disabled={upload.isPending}
            >
              {upload.isPending ? 'Nahrávám…' : '+ Rychlé nahrání'}
            </Button>
          </>
        }
      >
        <p className={styles.hint}>
          Galerie patří k aktivní stavbě z levého menu. Filtry odpovídají kategoriím a vazbám na deník / kontrolní den.
        </p>
        {error ? (
          <div style={{ padding: 24, borderRadius: 4, background: 'var(--sd-danger-soft)', color: 'var(--sd-danger)' }}>
            Chyba: {error instanceof Error ? error.message : 'neznámá'}
          </div>
        ) : (
          <>
            {filterToolbar}
            {selectionBar}
            {total === 0 && !isLoading && (
              <div className={styles.emptyBox}>
                {photos.length === 0
                  ? 'Zatím žádné fotografie — nahrajte první přes tlačítka výše.'
                  : 'V tomto filtru nic neodpovídá — zkuste „Vše“ nebo jinou kategorii.'}
              </div>
            )}

            {groups.map((g) => (
              <section key={g.key} className={styles.dateGroup}>
                <div className={styles.dateLabel}>
                  {g.key} · {g.items.length}
                </div>
                <div className={styles.grid}>
                  {g.items.map((p) => {
                    const isSel = selected.has(p.id);
                    const badge = KAT_LABEL[p.kategorie] ?? p.kategorie;
                    const badgeCls =
                      p.kategorie === 'prubeh'
                        ? styles.badge_prubeh
                        : p.kategorie === 'pred_po'
                          ? styles.badge_pred_po
                          : p.kategorie === 'vada'
                            ? styles.badge_vada
                            : styles.badge_ostatni;
                    return (
                      <div
                        key={p.id}
                        className={cx(styles.thumb, isSel && styles.thumbSelected)}
                        title={p.popis || p.filename}
                        onClick={() => openLightbox(p)}
                        role="button"
                      >
                        <PhotoThumb
                          url={`/api/stavby/${activeStavbaId}/photos/${p.id}/thumbnail`}
                          alt={p.popis || p.filename}
                        />
                        <button
                          type="button"
                          className={styles.thumbCheck}
                          aria-checked={isSel}
                          onClick={(e) => toggleSelect(p.id, e)}
                          title={isSel ? 'Odebrat z výběru' : 'Vybrat'}
                        >
                          {isSel ? '✓' : ''}
                        </button>
                        <span className={cx(styles.katBadge, badgeCls)}>{badge}</span>
                        <span className={styles.label}>{p.popis || p.filename}</span>
                      </div>
                    );
                  })}
                  <div
                    className={cx(styles.thumb, styles.empty)}
                    onClick={() => setUploadOpen(true)}
                    role="button"
                    style={{ cursor: 'pointer' }}
                  >
                    <span style={{ fontSize: 20 }}>+</span>
                    <span>přidat fotografii</span>
                  </div>
                </div>
              </section>
            ))}
          </>
        )}
      </ListPage>

      <Modal
        open={!!lightbox}
        onClose={() => setLightbox(null)}
        title={lightbox ? lightbox.filename : ''}
        footer={
          <>
            <Button variant="ghost" onClick={() => setLightbox(null)}>
              Zavřít
            </Button>
            <Button variant="secondary" onClick={handleLbDownload}>
              Stáhnout soubor
            </Button>
            <Button variant="secondary" onClick={handleLbDelete} disabled={deletePhoto.isPending}>
              Smazat
            </Button>
            <Button variant="primary" onClick={handleLbSave} disabled={updatePhoto.isPending}>
              Uložit popis / kategorii
            </Button>
          </>
        }
      >
        {lightbox && activeStavbaId && (
          <div className={styles.lightboxBody}>
            <PhotoFull
              url={`/api/stavby/${activeStavbaId}/photos/${lightbox.id}/full`}
              alt={lightbox.filename}
            />
            <div className={styles.lightboxFields}>
              <label className={styles.fieldLbl}>
                Popis
                <input
                  className={styles.fieldInp}
                  value={lbPopis}
                  onChange={(e) => setLbPopis(e.target.value)}
                />
              </label>
              <label className={styles.fieldLbl}>
                Kategorie
                <select className={styles.fieldInp} value={lbKat} onChange={(e) => setLbKat(e.target.value)}>
                  <option value="prubeh">Průběh</option>
                  <option value="pred_po">Před/Po</option>
                  <option value="vada">Vada</option>
                  <option value="ostatni">Ostatní</option>
                </select>
              </label>
              {lightbox.owner_name ? (
                <div className={styles.metaLine}>Nahrál: {lightbox.owner_name}</div>
              ) : null}
            </div>
          </div>
        )}
      </Modal>

      <Modal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        title="Nahrát fotografie"
        footer={
          <>
            <Button variant="ghost" onClick={() => setUploadOpen(false)}>
              Zrušit
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                uploadWithMetaRef.current = true;
                fileInput.current?.click();
              }}
              disabled={upload.isPending}
            >
              Vybrat soubory…
            </Button>
          </>
        }
      >
        <div className={styles.uploadForm}>
          <label className={styles.fieldLbl}>
            Kategorie
            <select className={styles.fieldInp} value={upKat} onChange={(e) => setUpKat(e.target.value)}>
              <option value="prubeh">Průběh</option>
              <option value="pred_po">Před/Po</option>
              <option value="vada">Vada</option>
              <option value="ostatni">Ostatní</option>
            </select>
          </label>
          <label className={styles.fieldLbl}>
            Datum pořízení
            <input
              type="date"
              className={styles.fieldInp}
              value={upDatum}
              onChange={(e) => setUpDatum(e.target.value)}
            />
          </label>
          <label className={styles.fieldLbl}>
            Společný popis (volitelně)
            <input
              className={styles.fieldInp}
              value={upPopis}
              onChange={(e) => setUpPopis(e.target.value)}
              placeholder="např. Hrubá stavba — západní průčelí"
            />
          </label>
          <p className={styles.uploadHint}>Po stisknutí „Vybrat soubory“ můžete označit více fotek najednou.</p>
        </div>
      </Modal>

      <Modal
        open={emailOpen}
        onClose={() => {
          setEmailOpen(false);
          setEmailMsg(null);
        }}
        title="Odeslat fotky e-mailem"
        footer={
          <>
            <Button variant="ghost" onClick={() => setEmailOpen(false)}>
              Zrušit
            </Button>
            <Button variant="primary" onClick={sendEmail} disabled={emailBusy || !emailTo.trim()}>
              {emailBusy ? 'Odesílám…' : 'Odeslat'}
            </Button>
          </>
        }
      >
        <div className={styles.uploadForm}>
          <p className={styles.uploadHint}>
            {selected.size > 0
              ? `Odešle se ${selected.size} vybraných fotek.`
              : `Odešle se všech ${filtered.length} fotek v aktuálním filtru (vyberte fotky, pokud chcete jen část).`}
          </p>
          <label className={styles.fieldLbl}>
            Komu (e-mail) *
            <input
              type="email"
              className={styles.fieldInp}
              value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
              required
            />
          </label>
          <label className={styles.fieldLbl}>
            Předmět
            <input
              className={styles.fieldInp}
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              placeholder={activeStavba ? `Fotodokumentace — ${activeStavba.nazev}` : ''}
            />
          </label>
          <label className={styles.fieldLbl}>
            Text zprávy
            <textarea
              className={styles.fieldTxt}
              rows={5}
              value={emailBody}
              onChange={(e) => setEmailBody(e.target.value)}
              placeholder="Dobrý den, v příloze zasíláme vybrané fotografie ze stavby."
            />
          </label>
          {emailMsg ? <div className={styles.emailMsg}>{emailMsg}</div> : null}
        </div>
      </Modal>
    </>
  );
}
